/* @flow */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Row } from 'react-grid-system';
import {
  Button,
  Checkbox,
  Images,
  InputClearable,
  LoadingIndicator,
  Modal,
  ModalTitle,
  PromptText,
} from 'listertech-commons-web';
import { callGetAgencies, callEnrollMany } from '../../api/agencies';
import { callGetOne } from '../../api/users';

const ModalButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  min-width: 400px;
  margin-top: 40px;

  > * {
    width: auto;
    padding: 0px 20px;
  }
`;

const Title = styled(ModalTitle)`
  display: inline-block;
`;

const TitleExtra = styled.p`
  display: inline-block;
  margin: 0;
  font-family: Montserrat;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  color: #9da8ae;
`;

const Label = styled(TitleExtra)`
  font-size: 16px;
`;

const Icon = styled.img`
  width: 18px;
  height: 18px;
  margin-left: 12px;
`;

const IconLabel = styled(Label)`
  margin-left: 12px;
`;

const List = styled.div`
  margin-top: 12px;
  max-height: 300px;
  overflow-y: scroll;
  overflow-x: hidden;
`;

const ListItem = styled.div`
  display: flex;
  margin-top: 12px;
`;

const AssignmentsModal = props => {
  const { contractor, onClose, onSearchUpdate } = props;

  const [agencies, setAgencies] = useState([]);
  const [allAgencies, setAllAgencies] = useState([]);
  const [enrolledAgencies, setEnrolledAgencies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [settled, setSettled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingEnroll, setLoadingEnroll] = useState(false);
  const [error, setError] = useState('');
  const [enrollAll, setEnrollAll] = useState(false);

  useEffect(() => {
    // Fetch agencies for all contractors if array, or just one otherwise
    if (contractor) {
      if (Array.isArray(contractor)) {
        Promise.all(contractor.map(c => fetchContractorAgencies(c._id)))
          .then(all => {
            // merge unique enrolled agencies across all contractors
            const merged = all.flat();
            const unique = [
              ...new Map(merged.map(item => [item.id, item])).values(),
            ];
            setEnrolledAgencies(unique);
          })
          .catch(() => setEnrolledAgencies([]));
      } else {
        fetchContractorAgencies(contractor._id);
      }
    }
  }, [contractor]);

  useEffect(() => {
    fetchAgencies();
  }, [enrolledAgencies]);

  useEffect(() => {
    if (settled) {
      fetchAgencies();
    }
  }, [searchTerm, settled]);

  const updateAgencies = agencies => {
    setError('');
    const allAgenciesList = agencies
      ? agencies.map(a => {
          const ea = enrolledAgencies.find(ea => ea.id === a._id); // Find the first agency in the enrolledAgencies list that has the same id as the current agency
          const na = enrolledAgencies.find(na => na.name === a.name); // Find the first agency in the enrolledAgencies list that has the same name as the current agency
          const state = ea ? ea.state : '';
          const icon =
            state &&
            {
              pending: Images.pending,
              accepted: Images.accepted,
              rejected: Images.rejected,
            }[state];
          return {
            ...a,
            name:
              state === 'accepted'
                ? a.name
                : state === 'rejected'
                ? a.name + '_rejected'
                : state == 'pending'
                ? a.name + '_pending'
                : !state && na
                ? a.name + '_pending'
                : a.name,
            checked: a.checked || state === 'accepted',
            disabled: Boolean(state === 'accepted'),
            state,
            icon,
          };
        })
      : [];
    const key = 'name';
    const uniqueAgencies = [
      ...new Map(allAgenciesList.map(item => [item[key], item])).values(),
    ];
    setAgencies(uniqueAgencies);
    setAllAgencies(allAgenciesList);
  };

  const updateAgencyChecked = (agency, checked) => {
    setError('');
    setAgencies(
      agencies.map(a => (a.name === agency.name ? { ...a, checked } : a)),
    );
  };

  const updateSearchTerm = searchTerm => {
    setLoading(true);
    setSearchTerm(searchTerm);
  };

  const fetchContractorAgencies = async (contractorId) => {
    try {
      const data = await callGetOne(contractorId);
      setEnrolledAgencies(
        data.enrolled_agencies.map(ea => ({
          id: ea.agency_id,
          state: ea.state,
          name: ea.name,
        })),
      );
    } catch (e) {
      setEnrolledAgencies([]);
    }
  };

  // const fetchAgencies = async () => {
  //   setLoading(true);
  //   setSettled(false);
  //   try {
  //     const response: Object = await callGetAgencies(
  //       30,
  //       0,
  //       'name',
  //       false,
  //       searchTerm,
  //     );
  //     updateAgencies(response.data);
  //     setLoading(false);
  //   } catch (e) {
  //     alert(`Error: ${e.message}`);
  //     setLoading(false);
  //   }
  // };

  const fetchAgencies = async () => {
    setLoading(true);
    setSettled(false);
    try {
      let page_number = 0;
      let all_agencies = [];
      while (true) {
        const response: Object = await callGetAgencies(
          30,
          page_number,
          'name',
          false,
          searchTerm,
        );
        const total_count = response.headers.count;
        all_agencies = [...all_agencies, ...response.data];
        if (total_count <= (page_number + 1) * 30) {
          break;
        }
        page_number = page_number + 1;
      }
      updateAgencies(all_agencies);
      setLoading(false);
    } catch (e) {
      alert(`Error: ${e.message}`);
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    setError('');
    const agencyNames = enrollAll
      ? agencies.filter(a => !a.disabled).map(a => a.name)
      : agencies
      .filter(a => a.checked && !a.disabled)
      .map(a => a.name);
    if (!agencyNames.length) {
      return setError('Select a new agency to enroll');
    }
    const contractorId = contractor._id;
    const agencyIds = enrollAll //if enrollAll checkbox is clicked, enroll user in every agency that isnt disabled
      ? allAgencies.filter(a => !a.disabled).map(a => a._id)
      : allAgencies
        .filter(obj => agencyNames.includes(obj.name))
        .map(obj => obj._id);
    try {
      setLoadingEnroll(true);

      //Support enrolling multiple contractors (from a Batch Selection)
      if (Array.isArray(contractor)) {
        await Promise.all(
          contractor.map(async c => {
            // Fetch current agencies for this contractor
            const data = await callGetOne(c._id);
            const enrolledIds = data.enrolled_agencies.filter(ea => ea.state !== 'rejected').map(ea => ea.agency_id);

            // Filter out agencies they already have
            const newAgencyIds = agencyIds.filter(id => !enrolledIds.includes(id));

            // Only enroll if there's at least one new agency to add
            if (newAgencyIds.length > 0) {
              await callEnrollMany(c._id, newAgencyIds);
            }
          })
        );
      } else {
        //If only one contractor, do the same thing
        const data = await callGetOne(contractor._id);
        const enrolledIds = data.enrolled_agencies.filter(ea => ea.state !== 'rejected').map(ea => ea.agency_id);
        const newAgencyIds = agencyIds.filter(id => !enrolledIds.includes(id));

        if (newAgencyIds.length > 0) {
          await callEnrollMany(contractor._id, newAgencyIds);
        }
      }

      setLoadingEnroll(false);
      onClose();
    } catch (e) {
      setError(e.message);
      setLoadingEnroll(false);
    }
  };

  return (
    <Modal visible>
      <Row justify="space-between" align="baseline">
        <Title>Choose Assignments</Title>
        <TitleExtra>
          {Array.isArray(contractor)
            ? contractor.map(c => c.name).join(', ')
            : contractor && contractor.name}
        </TitleExtra>
      </Row>
      <Row align="center" style={{ marginTop: '30px' }}>
        <Label>Search</Label>
        <InputClearable
          containerStyle={{ flex: 1, width: 'auto', marginLeft: '15px' }}
          value={searchTerm}
          onChange={ev => updateSearchTerm(ev.target.value)}
          onClear={() => updateSearchTerm('')}
          onSettled={() => setSettled(true)}
        />
      </Row>
      <Row style={{ marginTop: '20px' }}>
        <Checkbox
          label="Enroll All Agencies"
          checked={enrollAll}
          onCheckedChange={setEnrollAll}
        />
      </Row>
      {loading ? (
        <LoadingIndicator style={{ marginTop: '12px' }} />
      ) : agencies.length ? (
        <List>
          {agencies.map(it => (
            <ListItem key={`agency-${it.name}`}>
              <Checkbox
                label={it.name}
                checked={it.checked}
                disabled={it.disabled}
                onCheckedChange={checked => updateAgencyChecked(it, checked)}
              />
              {it.icon ? (
                <>
                  <Icon src={it.icon} />
                  <IconLabel>({it.state})</IconLabel>
                </>
              ) : null}
            </ListItem>
          ))}
        </List>
      ) : (
        <Label style={{ marginTop: '12px', color: '#354148' }}>
          No agencies found
        </Label>
      )}
      <ModalButtons>
        <Button noMargin theme="light" onClick={onClose}>
          Cancel
        </Button>
        <Button noMargin onClick={handleEnroll} loading={loadingEnroll}>
          Enroll
        </Button>
      </ModalButtons>
      {error ? (
        <PromptText style={{ marginTop: '12px', textAlign: 'center' }}>
          {error}
        </PromptText>
      ) : null}
    </Modal>
  );
};

export default AssignmentsModal;
